import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Users,
  Trash2,
  Loader2,
  Shield,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamPage() {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  const [newMember, setNewMember] = useState({
    email: '',
    password: '',
    name: '',
    role: 'member'
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const response = await axios.post(`${API}/team`, newMember);
      setMembers([...members, response.data]);
      setNewMember({ email: '', password: '', name: '', role: 'member' });
      setAddDialogOpen(false);
      toast.success('Team member added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setAdding(false);
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await axios.delete(`${API}/team/${id}`);
      setMembers(members.filter(m => m.id !== id));
      toast.success('Team member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-gray-100" />
          <Skeleton className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Team</h1>
            <p className="text-gray-500 mt-1">Manage team members in your organization</p>
          </div>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="font-heading text-gray-900">Add Team Member</DialogTitle>
                <DialogDescription className="text-gray-500">
                  Add a new user to your team. They'll share the same voice agents and data.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={addMember} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Name</Label>
                  <Input
                    placeholder="e.g., John Doe"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    type="email"
                    placeholder="e.g., john@company.com"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Password</Label>
                  <Input
                    type="password"
                    placeholder="Set a password"
                    value={newMember.password}
                    onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Role</Label>
                  <select
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={adding}>
                    {adding && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Add Member
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Team Members ({members.length})
            </CardTitle>
            <CardDescription className="text-gray-500">
              All members share the same voice agents and tenant-level data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-300 hover:bg-transparent">
                  <TableHead className="text-gray-500 text-xs">Name</TableHead>
                  <TableHead className="text-gray-500 text-xs">Email</TableHead>
                  <TableHead className="text-gray-500 text-xs">Role</TableHead>
                  <TableHead className="text-gray-500 text-xs">Joined</TableHead>
                  <TableHead className="text-gray-500 text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id} className="border-gray-300 hover:bg-gray-50">
                    <TableCell className="text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                          {member.name?.[0]?.toUpperCase() || member.email[0].toUpperCase()}
                        </div>
                        {member.name || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-gray-700">{member.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        member.role === 'admin'
                          ? 'bg-purple-50 text-purple-600 border-purple-200'
                          : 'bg-gray-100 text-gray-600 border-gray-300'
                      }>
                        {member.role === 'admin' ? (
                          <><Shield className="w-3 h-3 mr-1" /> Admin</>
                        ) : (
                          <><User className="w-3 h-3 mr-1" /> Member</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500 text-xs">
                      {format(new Date(member.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      {member.id !== currentUser?.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-500 hover:text-rose-400"
                          onClick={() => removeMember(member.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
